import { AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { SectionThread } from "../components/Flourish";
import { LoadingScreen } from "../components/LoadingScreen";
import { Centers } from "../components/sections/Centers";
import { Contact } from "../components/sections/Contact";
import { Hero } from "../components/sections/Hero";
import { Impact } from "../components/sections/Impact";
import { Mission } from "../components/sections/Mission";
import { Newsletters } from "../components/sections/Newsletters";
import { Stories } from "../components/sections/Stories";
import { Team } from "../components/sections/Team";
import { Trust } from "../components/sections/Trust";

const MIN_SPLASH_MS = 2300; // let the logo build animation finish
const MAX_SPLASH_MS = 4500; // never trap users on the splash

// Show the brand splash only on the first visit to the home route per browser
// tab. Returning here from /gallery should not replay the intro.
const SPLASH_SHOWN_KEY = "se:splash-shown";

function splashAlreadyShown(): boolean {
  try {
    return sessionStorage.getItem(SPLASH_SHOWN_KEY) === "1";
  } catch {
    return false;
  }
}

export function HomePage() {
  const location = useLocation();
  const [skipSplash] = useState(splashAlreadyShown);
  const [videoReady, setVideoReady] = useState(false);
  const [animationDone, setAnimationDone] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [forceDismiss, setForceDismiss] = useState(false);

  useEffect(() => {
    try {
      sessionStorage.setItem(SPLASH_SHOWN_KEY, "1");
    } catch {
      /* sessionStorage unavailable — splash simply shows each load */
    }
    if (skipSplash) {
      return;
    }
    const onReady = () => setVideoReady(true);
    window.addEventListener("se:hero-video-ready", onReady);
    const minTimer = window.setTimeout(
      () => setMinTimeElapsed(true),
      MIN_SPLASH_MS,
    );
    const maxTimer = window.setTimeout(
      () => setForceDismiss(true),
      MAX_SPLASH_MS,
    );
    return () => {
      window.removeEventListener("se:hero-video-ready", onReady);
      window.clearTimeout(minTimer);
      window.clearTimeout(maxTimer);
    };
  }, [skipSplash]);

  const splashVisible =
    !skipSplash &&
    !forceDismiss &&
    (!minTimeElapsed || !animationDone || !videoReady);

  // Lock body scroll while splash is up, restore after.
  useEffect(() => {
    if (splashVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [splashVisible]);

  // Cross-route anchor navigation: a nav link on another page (e.g. /gallery)
  // routes home with a target section, which we scroll to once mounted.
  useEffect(() => {
    // SAFETY: router state is set only by this app's own navigate calls, which pass { scrollTo }.
    const target = (location.state as { scrollTo?: string } | null)?.scrollTo;
    if (!target) {
      return;
    }
    const el = document.querySelector(target);
    if (!el) {
      return;
    }
    requestAnimationFrame(() =>
      el.scrollIntoView({ behavior: splashVisible ? "auto" : "smooth" }),
    );
    // Only re-run when the incoming navigation target changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  return (
    <>
      <AnimatePresence>
        {splashVisible && (
          <LoadingScreen
            key="loading"
            onComplete={() => setAnimationDone(true)}
          />
        )}
      </AnimatePresence>
      <main>
        <Hero />
        <Mission />
        <SectionThread />
        <Stories />
        <SectionThread />
        <Impact />
        <SectionThread />
        <Centers />
        <SectionThread />
        <Team />
        <SectionThread />
        <Trust />
        <SectionThread />
        <Newsletters />
        <Contact />
      </main>
    </>
  );
}
